//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import { Atom } from '@effect-atom/atom';
import * as Context from 'effect/Context';
import type * as Effect$ from 'effect/Effect';
import type * as Layer$ from 'effect/Layer';
import * as Schema$ from 'effect/Schema';

import type { AiModelResolver as AiModelResolver$ } from '@dxos/ai';
import type { OpaqueToolkit } from '@dxos/ai';
import * as Capability$ from '@dxos/app-framework/Capability';
import type { BuilderExtensions, GraphBuilder } from '@dxos/app-graph';
import * as Credential from '@dxos/compute/Credential';
import * as Operation from '@dxos/compute/Operation';
import * as Skill from '@dxos/compute/Skill';
import type { Database, Type } from '@dxos/echo';
import { type Translator as Translator$ } from '@dxos/i18n';
import { type URI } from '@dxos/keys';
import { Progress } from '@dxos/progress';
import type { AnchoredTo } from '@dxos/types';
import type { Position } from '@dxos/util';

// eslint-disable-next-line @dxos/rules/import-as-namespace
import type * as Translations$ from '../app/Translations';

export const LAYOUT_CAPABILITY_ID = 'org.dxos.app-framework.capability.layout';

// TODO(burdon): See Accept attribute (uses MIME types).
// E.g., 'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
export const defaultFileTypes = {
  images: ['png', 'jpg', 'jpeg', 'gif'],
  media: ['mp3', 'mp4', 'mov', 'avi'],
  text: ['pdf', 'txt', 'md'],
};

export const FileInfoSchema = Schema$.Struct({
  name: Schema$.String,
  type: Schema$.String,
  url: Schema$.optional(Schema$.String),
  cid: Schema$.optional(Schema$.String),
});

export type FileInfo = Schema$.Schema.Type<typeof FileInfoSchema>;

export type Layout = Readonly<{
  mode: string;
  dialogOpen: boolean;
  sidebarOpen: boolean;
  complementarySidebarOpen: boolean;
  /**
   * The id of the active workspace, where a workspace is a set of active items.
   */
  workspace: string;
  /**
   * Identifiers of items which are currently active in the application.
   */
  active: string[];
  /**
   * Identifiers of items which were previously active in the application.
   */
  inactive: string[];
  /**
   * Identifier of the item which should be scrolled into view.
   */
  scrollIntoView: string | undefined;
}>;

/**
 * Layout capability - provides reactive access to the current layout state.
 * @category Capability
 */
export const Layout = Capability$.makeSingleton<Atom.Atom<Layout>>()(LAYOUT_CAPABILITY_ID);

/**
 * @category Capability
 */
export const Translations = Capability$.make<Readonly<Translations$.Resource[]>>()(
  'org.dxos.app-framework.capability.translations',
);

/**
 * Provides access to the shared, framework-agnostic translation instance so non-React code
 * (operations, services, Effect programs) can translate strings dynamically.
 * @category Capability
 */
export const Translator = Capability$.makeSingleton<Translator$>()('org.dxos.app-framework.capability.translator');

/**
 * Effect service for the {@link Translator} capability, consumable via `yield* TranslatorService`
 * once {@link translatorLayer} is provided.
 */
export class TranslatorService extends Context.Tag('@dxos/app-toolkit/TranslatorService')<
  TranslatorService,
  Translator$
>() {}

/**
 * Layer that resolves {@link TranslatorService} from the {@link Translator} capability.
 */
export const translatorLayer: Layer$.Layer<TranslatorService, never, Capability$.Service> = Capability$.asLayer(
  Translator,
  TranslatorService,
);

/** A writer scoped to a single plugin's compartment — the only way to mutate the stats store. */
export type StatsCompartment = Readonly<{
  /** Replace this compartment's value. */
  set: (stats: unknown) => void;
  /** Remove this compartment. */
  clear: () => void;
}>;

/**
 * A generic, reactive store for stats/telemetry, partitioned into one compartment per plugin. Every
 * consumer can READ the entire store (all compartments) reactively, but can only WRITE its own
 * compartment — obtained via `compartment(pluginKey)` with the plugin's own key (`meta.profile.key`).
 * Currently in-memory (discarded on reload); persistence may be layered on later, so the name is not
 * tied to transience. Contributed by a host plugin (plugin-debug) — writers resolve it with
 * `Capability.getAll` so writing is a no-op when no host is loaded (e.g. production without devtools).
 * @category Capability
 */
export type StatsPanelStore = Readonly<{
  /** Reactive map of all compartments keyed by plugin key (full read access). */
  statsAtom: Atom.Writable<Record<string, unknown>>;
  /** Read one plugin's compartment. */
  get: (pluginKey: string) => unknown;
  /** A writer scoped to one plugin's compartment — the only write path. */
  compartment: (pluginKey: string) => StatsCompartment;
}>;

/**
 * @category Capability
 */
export const StatsPanel = Capability$.makeSingleton<StatsPanelStore>()('org.dxos.app-toolkit.capability.statsPanel');

/**
 * The graph builder instance. Its `graph` is the reactive node tree; the builder also carries the
 * `urlKey` declarations and node→extension provenance that URL resolution (`@dxos/app-graph`'s
 * `path-resolution.ts`) reads and reverse-maps — neither derivable from `graph` alone.
 */
export type AppGraph = GraphBuilder.GraphBuilder;

/**
 * @category Capability
 */
export const AppGraph = Capability$.makeSingleton<AppGraph>()('org.dxos.app-framework.capability.appGraph');

/**
 * @category Capability
 */
export const AppGraphBuilder = Capability$.make<BuilderExtensions>()(
  'org.dxos.app-framework.capability.appGraphBuilder',
);

export type Settings = {
  prefix: string;
  // Settings are persisted as plain atoms, so the schema is always context-free
  // (`R = never`); this lets a schema-driven form decode/validate it directly.
  schema: Schema$.Schema.AnyNoContext;
  atom: Atom.Writable<any>;
};

/**
 * Type guard to check if a value is a Settings object.
 */
export const isSettings = (value: unknown): value is Settings =>
  typeof value === 'object' &&
  value !== null &&
  'prefix' in value &&
  typeof (value as Settings).prefix === 'string' &&
  'atom' in value &&
  Atom.isAtom(value.atom) &&
  Atom.isWritable(value.atom);

/**
 * @category Capability
 */
export const Settings = Capability$.make<Settings>()('org.dxos.app-framework.capability.settings');

export type Schema = ReadonlyArray<Type.AnyEntity>;

/**
 * @category Capability
 */
export const Schema = Capability$.make<Schema>()('org.dxos.app-framework.capability.schema');

export type Toolkit = OpaqueToolkit.OpaqueToolkit;

/**
 * @category Capability
 */
export const Toolkit = Capability$.make<Toolkit>()('org.dxos.app-framework.capability.aiToolkit');

/**
 * @category Capability
 */
export const SkillDefinition = Capability$.make<Skill.Definition>()(
  'org.dxos.app-framework.capability.skillDefinition',
);

/**
 * A static asset bundled with a plugin's published package, exposed for
 * other plugins to read.
 *
 * Contributors import the raw file (e.g. `import spec from '../PLUGIN.mdl?raw'`)
 * and contribute it via this capability from a dependency-mode module. Consumers
 * read all contributions with `Capability.getAll(AppCapabilities.PluginAsset)`.
 */
export type PluginAsset = Readonly<{
  /** Owning plugin id (matches `Plugin.Meta.id`). */
  pluginId: string;
  /** Path within the plugin package — typically equal to `Plugin.Meta.spec`. */
  path: string;
  /** Raw text content. */
  content: string;
  /** Optional MIME type (e.g. `text/markdown`, `application/x-mdl`). */
  mimeType?: string;
}>;

/**
 * @category Capability
 */
export const PluginAsset = Capability$.make<PluginAsset>()('org.dxos.app-framework.capability.pluginAsset');

/**
 * Plugins can contribute model resolvers. The `Credential.CredentialsService` requirement is
 * supplied by the active-space resolver — BYOK-aware resolvers wrap their HTTP client with
 * `Header.byokLayer(...)`; the rest carry it through unused.
 */
export const AiModelResolver = Capability$.make<
  Layer$.Layer<AiModelResolver$.AiModelResolver, never, Credential.CredentialsService>
>()('org.dxos.app-framework.capability.aiModelResolver');

export type FileUploader = (db: Database.Database, file: File) => Promise<FileInfo | undefined>;

/**
 * @category Capability
 */
export const FileUploader = Capability$.make<FileUploader>()('org.dxos.app-framework.capability.fileUploader');

export type AnchorSort = {
  key: string;
  sort: (anchorA: AnchoredTo.AnchoredTo, anchorB: AnchoredTo.AnchoredTo) => number;
};

/**
 * @category Capability
 */
export const AnchorSort = Capability$.make<AnchorSort>()('org.dxos.app-framework.capability.anchorSort');

/** Anchor→text resolution contributed per typename by plugins whose objects support cursor-range anchors. */
export type AnchorResolver = Readonly<{
  key: string;
  /** Resolve an anchor (`"${fromCursor}:${toCursor}"`) to the text it spans, or `undefined` when unresolvable. */
  getText: (obj: any, anchor: string) => string | undefined;
}>;

/**
 * @category Capability
 */
export const AnchorResolver = Capability$.make<AnchorResolver>()('org.dxos.app-framework.capability.anchorResolver');

/** Text content extractor contributed per typename by plugins that support text extraction. */
export type TextContent = Readonly<{
  id: string;
  getTextContent: (object: any) => Promise<string | undefined>;
}>;

/**
 * @category Capability
 */
export const TextContent = Capability$.make<TextContent>()('org.dxos.app-framework.capability.textContent');

/** Comment configuration contributed per typename by plugins that support commenting. */
export type CommentConfig = Readonly<{
  id: string;
  comments: 'anchored' | 'unanchored';
  selectionMode?: string;
  scrollToAnchor?: Operation.Definition.Any;
}>;

/**
 * @category Capability
 */
export const CommentConfig = Capability$.make<CommentConfig>()('org.dxos.app-framework.capability.commentConfig');

export type NavigationTarget = {
  /** Navigation path usable with the Open operation. */
  path: string;
  /** Human-readable label. */
  label: string;
  /** Object type. */
  type: string;
  /**
   * Sort order among the targets resolved for one query. A resolver that only produces a generic path —
   * the database subtree, which guarantees every ECHO object *a* path but is not where the tree shows
   * it — declares `Position.last`, so a resolver that knows the object's canonical home (its collection,
   * a type section) is preferred. Only the resolver knows how specific its answer is, hence a declared
   * position rather than path sniffing by the caller.
   */
  position?: Position.Position;
};

export type NavigationQuery = {
  uri?: URI.URI;
};

/**
 * Resolves a query to navigation targets.
 * Each plugin interprets the query and returns matching targets.
 * When called without a query, returns the plugin's default navigable pages.
 *
 * Requires `Database.Service`: a resolver turns an object URI into a path, which means loading the
 * object. The caller derives the database from the query's space and provides the layer — see
 * `NavigationOperation.ResolveNavigationTargets`, the one entry point that runs every resolver.
 * @category Capability
 */
export type NavigationTargetResolver = (
  query?: NavigationQuery,
) => Effect$.Effect<NavigationTarget[], never, Database.Service>;

export const NavigationTargetResolver = Capability$.make<NavigationTargetResolver>()(
  'org.dxos.app-framework.capability.navigationTargetResolver',
);

/**
 * Loads/verifies a navigation target by its `(spaceId, entityId)` so graph resolution can materialize
 * its node. Contributed by the plugin that owns object storage (plugin-client), consumed by layout
 * plugins — this is the abstraction that keeps layout plugins from depending on the client for
 * loading. `load` loads the object into local ECHO when present locally (so a URL-driven restore
 * materializes the plank's node), and resolves `true` if the object exists locally or, as a fallback,
 * remotely. A remote-only object resolves `true` but cannot render until it replicates locally.
 * @category Capability
 */
export type NavigationTargetLoader = Readonly<{
  id: string;
  load: (target: { spaceId: string; entityId: string }) => Effect$.Effect<boolean>;
}>;

export const NavigationTargetLoader = Capability$.make<NavigationTargetLoader>()(
  'org.dxos.app-framework.capability.navigationTargetLoader',
);

/**
 * Handler called by layout plugins on navigation events (page load, popstate, deep link).
 * Plugins contribute handlers to react to URL query params or other URL parts
 * without the layout plugin needing to know about specific params.
 * @category Capability
 */
export type NavigationHandler = (url: URL) => Effect$.Effect<void>;

export const NavigationHandler = Capability$.make<NavigationHandler>()(
  'org.dxos.app-toolkit.capability.navigationHandler',
);

/** A transient progress monitor handle — the update side of one registry entry. */
export type ProgressMonitor = Progress.TaskHandle;

/**
 * A registry of live progress providers, exposed as reactive atoms. Producers `register` a monitor
 * (keyed by a stable `name`, with a display `label`), advance/complete it, and `remove()` it when
 * done. Consumers read the aggregate `snapshotAtom` (e.g. the R0 rail popover) or a single provider's
 * `monitorAtom(name)` (e.g. an article's inline meter). Contributed by an always-loaded host
 * (`plugin-progress`); backed by the shared `@dxos/progress` core.
 */
export type ProgressRegistry = Readonly<{
  /** Aggregate snapshot of all active providers. */
  snapshotAtom: Atom.Atom<Progress.ProgressSnapshot>;
  /** One provider's reactive state, by name (stable/memoized per name). */
  monitorAtom: (name: string) => Atom.Atom<Progress.TaskProgress | undefined>;
  /**
   * Register (or resume) a provider and mark it running. Pass `onCancel` to make it cancellable — the
   * meter then shows a cancel control that invokes {@link ProgressRegistry.cancel}.
   */
  register: (name: string, options?: { label?: string; total?: number; onCancel?: () => void }) => ProgressMonitor;
  /** Invoke a provider's registered `onCancel` handler (no-op if it is not cancellable). */
  cancel: (name: string) => void;
  /** Non-reactive read of the current snapshot. */
  snapshot: () => Progress.ProgressSnapshot;
}>;

/**
 * @category Capability
 */
export const ProgressRegistry = Capability$.makeSingleton<ProgressRegistry>()(
  'org.dxos.app-toolkit.capability.progressRegistry',
);
